// @ts-check
/**
 * Typed client for the telehealth voice (Twilio Device SDK) API.
 *
 * Follows the same pattern as providerMessaging.js / videoConferencing.js:
 * thin async functions per operation, types aliased from the generated schema.
 *
 * Uses the global axios instance (baseURL, withCredentials, tenant_slug
 * interceptor all applied by main.js).
 */
import axios from 'axios'

const BASE = '/api/work/telehealth'
const PHONE_CALLS = '/api/telehealth/phone-calls'

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {Schemas['VoiceTokenResponse']} VoiceTokenResponse */
/** @typedef {Schemas['TelehealthVoicemail']} TelehealthVoicemail */
/** @typedef {Schemas['TelehealthPhoneSessionDetail']} TelehealthPhoneSessionDetail */
/** @typedef {Schemas['PhoneLookupResponse']} PhoneLookupResponse */
/** @typedef {Schemas['CreatePhoneEngagementRequest']} CreatePhoneEngagementRequest */
/** @typedef {Schemas['CreatePhoneEngagementResponse']} CreatePhoneEngagementResponse */
/** @typedef {Schemas['CreateClinicalSessionRequest']} CreateClinicalSessionRequest */
/** @typedef {Schemas['CreateClinicalSessionResponse']} CreateClinicalSessionResponse */
/** @typedef {Schemas['TelehealthPhoneSessionCallQuality']} TelehealthPhoneSessionCallQuality */
/** @typedef {Schemas['CallAudioPrepareResponse']} CallAudioPrepareResponse */
/** @typedef {Schemas['CallAudioExistsResponse']} CallAudioExistsResponse */
/** @typedef {Schemas['AddConferenceParticipantRequest']} AddConferenceParticipantRequest */
/** @typedef {Schemas['AddConferenceParticipantResponse']} AddConferenceParticipantResponse */
/** @typedef {Schemas['RemoveConferenceParticipantRequest']} RemoveConferenceParticipantRequest */
/** @typedef {Schemas['HoldConferenceParticipantRequest']} HoldConferenceParticipantRequest */
/** @typedef {Schemas['ConferenceParticipantsResponse']} ConferenceParticipantsResponse */
/** @typedef {Schemas['CallContextResponse']} CallContextResponse */
/** @typedef {Schemas['OutboundCallStatusResponse']} OutboundCallStatusResponse */
/** @typedef {Schemas['LinkCallToEngagementRequest']} LinkCallToEngagementRequest */
/** @typedef {Schemas['LinkCallToEngagementResponse']} LinkCallToEngagementResponse */
/** @typedef {Schemas['InboundCall']} InboundCall */
/** @typedef {Schemas['InboundCallWorkResponse']} InboundCallWorkResponse */
/** @typedef {import('./schema').operations['inbound_call_by_call_sid']['parameters']['query']} InboundCallByCallSidQuery */
/** @typedef {Schemas['TelehealthParticipantRole']} TelehealthParticipantRole */
/** @typedef {Schemas['PhoneCallCreateRequest']} PhoneCallCreateRequest */
/** @typedef {Schemas['LiveTranscriptResponse']} LiveTranscriptResponse */
/** @typedef {Schemas['PhoneCall']} PhoneCall */
/** @typedef {Schemas['PhoneCallTranscriptSegment']} PhoneCallTranscriptSegment */
/** @typedef {Record<string, unknown>} PhoneCallDetailsUpdate */
/** @typedef {import('./schema').operations['telehealth_participant_roles_list']['parameters']['query']} ParticipantRolesQuery */
/** @typedef {Schemas['TelehealthPhoneSessionSearchRequest']} TelehealthPhoneSessionSearchRequest */
/** @typedef {Schemas['TelehealthPhoneSessionSearchResponse']} TelehealthPhoneSessionSearchResponse */

/**
 * POST /api/work/telehealth/voice/token/ — get a Twilio AccessToken with VoiceGrant.
 * @returns {Promise<VoiceTokenResponse>}
 */
export async function getVoiceToken() {
  const { data } = await axios.post(`${BASE}/voice/token/`)
  return data
}

/**
 * GET /api/work/telehealth/outbound-call-status/?call_sid= — patient-leg state for the current provider's outbound call.
 * @param {string} callSid
 * @returns {Promise<OutboundCallStatusResponse>}
 */
export async function getOutboundCallStatus(callSid) {
  const { data } = await axios.get(`${BASE}/outbound-call-status/`, {
    params: { call_sid: callSid },
  })
  return data
}

/**
 * POST /api/work/telehealth/provider-decline/ — record that the provider pressed Decline
 * before the Twilio Client SDK reports its call leg as busy.
 * @param {string} callSid
 * @returns {Promise<void>}
 */
export async function markProviderDecline(callSid) {
  await axios.post(`${BASE}/provider-decline/`, { call_sid: callSid })
}

const VOICEMAILS = '/api/voicemails'

/**
 * GET /api/voicemails/ — list voicemails for the current user.
 * @returns {Promise<TelehealthVoicemail[]>}
 */
export async function listVoicemails(params) {
  const { data } = await axios.get(`${VOICEMAILS}/`, { params })
  return data
}

/**
 * POST /api/voicemails/{uuid}/mark-read/
 * @param {string} uuid
 * @returns {Promise<void>}
 */
export async function markVoicemailRead(uuid) {
  await axios.post(`${VOICEMAILS}/${uuid}/mark-read/`)
}

/**
 * POST /api/voicemails/{uuid}/mark-unread/
 * @param {string} uuid
 * @returns {Promise<void>}
 */
export async function markVoicemailUnread(uuid) {
  await axios.post(`${VOICEMAILS}/${uuid}/mark-unread/`)
}

/**
 * POST /api/voicemails/{uuid}/archive/
 * @param {string} uuid
 * @returns {Promise<void>}
 */
export async function archiveVoicemail(uuid) {
  await axios.post(`${VOICEMAILS}/${uuid}/archive/`)
}

/**
 * POST /api/voicemails/{uuid}/unarchive/
 * @param {string} uuid
 * @returns {Promise<void>}
 */
export async function unarchiveVoicemail(uuid) {
  await axios.post(`${VOICEMAILS}/${uuid}/unarchive/`)
}

/**
 * PATCH /api/voicemails/{uuid}/update-description/
 * @param {string} uuid
 * @param {string} description
 * @returns {Promise<void>}
 */
export async function updateVoicemailDescription(uuid, description) {
  await axios.patch(`${VOICEMAILS}/${uuid}/update-description/`, { description })
}

/**
 * GET /api/voicemails/ — count of unread voicemails (fetches list, counts client-side).
 * @returns {Promise<number>}
 */
export async function getUnreadVoicemailCount() {
  const data = await listVoicemails()
  return data.filter((vm) => !vm.is_read).length
}

const PHONE_SESSIONS = '/api/telehealth-phone-sessions'

/**
 * POST /api/telehealth-phone-sessions/search/ — the current user's inbound and outbound calls, newest first.
 * @param {Partial<TelehealthPhoneSessionSearchRequest>} [body]
 * @returns {Promise<TelehealthPhoneSessionSearchResponse>}
 */
export async function searchMyPhoneSessions(body = {}) {
  const { data } = await axios.post(`${PHONE_SESSIONS}/search/`, body)
  return data
}

/**
 * GET /api/telehealth-phone-sessions/{uuid}/ — phone session detail with participants (retrieve only).
 * @param {string} uuid
 * @returns {Promise<TelehealthPhoneSessionDetail>}
 */
export async function getPhoneSession(uuid) {
  const { data } = await axios.get(`${PHONE_SESSIONS}/${uuid}/`)
  return data
}

/**
 * GET /api/telehealth-phone-sessions/{uuid}/call-quality/ — Twilio Voice Insights per participant (admins and manager viewers).
 * @param {string} uuid
 * @returns {Promise<TelehealthPhoneSessionCallQuality>}
 */
export async function getCallQuality(uuid) {
  const { data } = await axios.get(`${PHONE_SESSIONS}/${uuid}/call-quality/`)
  return data
}

/**
 * POST /api/telehealth-phone-sessions/{uuid}/prepare-call-audio/ — prepare the call recording for playback.
 * @param {string} uuid
 * @returns {Promise<CallAudioPrepareResponse | CallAudioExistsResponse>}
 */
export async function prepareCallAudio(uuid) {
  const { data, status } = await axios.post(`${PHONE_SESSIONS}/${uuid}/prepare-call-audio/`)
  return { ...data, _status: status }
}

/**
 * GET /api/telehealth-phone-sessions/{uuid}/call-audio/ — stream the call recording.
 * @param {string} uuid
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<Blob>}
 */
export async function getCallAudio(uuid, config) {
  const { data } = await axios.get(`${PHONE_SESSIONS}/${uuid}/call-audio/`, {
    responseType: 'blob',
    ...config,
  })
  return data
}

// ─── Phone → Clinical Engagement ──────────────────────────────────────────

/**
 * POST /api/work/telehealth/phone-lookup/
 * @param {string} phone — E.164 or digits-only
 * @returns {Promise<PhoneLookupResponse>}
 */
export async function lookupByPhone(phone) {
  const { data } = await axios.post(`${BASE}/phone-lookup/`, { phone })
  return data
}

/**
 * POST /api/work/telehealth/create-phone-engagement/
 * @param {{ entityId: number, callSid?: string }} params
 * @returns {Promise<CreatePhoneEngagementResponse>}
 */
export async function createPhoneEngagement({ entityId, callSid }) {
  const { data } = await axios.post(`${BASE}/create-phone-engagement/`, {
    entity_id: entityId,
    call_sid: callSid,
  })
  return data
}

/**
 * POST /api/work/create-clinical-session/ — starts an engagement session (clinical unless workTypeId is given).
 * @param {{ opportunityId: number, taskId?: number, workTypeId?: number }} params
 * @returns {Promise<CreateClinicalSessionResponse>}
 */
export async function createClinicalSession({ opportunityId, taskId, workTypeId }) {
  /** @type {CreateClinicalSessionRequest} */
  const body = { opportunity_id: opportunityId, task_id: taskId }
  if (workTypeId) body.work_type_id = workTypeId
  const { data } = await axios.post('/api/work/create-clinical-session/', body)
  return data
}

// ─── Conference Calling ─────────────────────────────────────────────────

/**
 * GET /api/work/telehealth/participant-roles/ — active participant roles for this tenant (provider excluded).
 * @param {ParticipantRolesQuery} [params]
 * @returns {Promise<TelehealthParticipantRole[]>}
 */
export async function listParticipantRoles(params) {
  const { data } = await axios.get(`${BASE}/participant-roles/`, { params })
  return data
}

/**
 * POST /api/work/telehealth/conference/{phoneSessionUuid}/add-participant/
 * @param {string} phoneSessionUuid
 * @param {AddConferenceParticipantRequest} body
 * @returns {Promise<AddConferenceParticipantResponse>}
 */
export async function addConferenceParticipant(phoneSessionUuid, body) {
  const { data } = await axios.post(
    `${BASE}/conference/${phoneSessionUuid}/add-participant/`,
    body,
  )
  return data
}

/**
 * POST /api/work/telehealth/conference/{phoneSessionUuid}/remove-participant/
 * @param {string} phoneSessionUuid
 * @param {RemoveConferenceParticipantRequest} body
 * @returns {Promise<void>}
 */
export async function removeConferenceParticipant(phoneSessionUuid, body) {
  await axios.post(
    `${BASE}/conference/${phoneSessionUuid}/remove-participant/`,
    body,
  )
}

/**
 * POST /api/work/telehealth/conference/{phoneSessionUuid}/hold-participant/
 * @param {string} phoneSessionUuid
 * @param {HoldConferenceParticipantRequest} body
 * @returns {Promise<void>}
 */
export async function holdConferenceParticipant(phoneSessionUuid, body) {
  await axios.post(
    `${BASE}/conference/${phoneSessionUuid}/hold-participant/`,
    body,
  )
}

/**
 * POST /api/work/telehealth/conference/{phoneSessionUuid}/leave-call/
 * Auto-unholds held participants, then removes the provider from the conference.
 * @param {string} phoneSessionUuid
 * @returns {Promise<void>}
 */
export async function leaveCall(phoneSessionUuid) {
  await axios.post(`${BASE}/conference/${phoneSessionUuid}/leave-call/`)
}

/**
 * POST /api/work/telehealth/conference/{phoneSessionUuid}/end-call/ — end the conference for everyone on it.
 * @param {string} phoneSessionUuid
 * @returns {Promise<void>}
 */
export async function endCallForEveryone(phoneSessionUuid) {
  await axios.post(`${BASE}/conference/${phoneSessionUuid}/end-call/`)
}

/**
 * GET /api/work/telehealth/conference/{phoneSessionUuid}/participants/
 * @param {string} phoneSessionUuid
 * @returns {Promise<ConferenceParticipantsResponse>}
 */
export async function getConferenceParticipants(phoneSessionUuid) {
  const { data } = await axios.get(
    `${BASE}/conference/${phoneSessionUuid}/participants/`,
  )
  return data
}

/**
 * GET /api/work/telehealth/call-context/{conferenceName}/
 * @param {string} conferenceName
 * @returns {Promise<CallContextResponse>}
 */
export async function getCallContext(conferenceName) {
  const { data } = await axios.get(`${BASE}/call-context/${conferenceName}/`)
  return data
}

/**
 * POST /api/work/telehealth/link-call-to-engagement/
 * @param {{ phoneSessionUuid: string, entityId: number }} params
 * @returns {Promise<LinkCallToEngagementResponse>}
 */
export async function linkCallToEngagement({ phoneSessionUuid, entityId }) {
  const { data } = await axios.post(`${BASE}/link-call-to-engagement/`, {
    phone_session_uuid: phoneSessionUuid,
    entity_id: entityId,
  })
  return data
}

/**
 * POST /api/telehealth/phone-calls/ — create the empty phone_call Work before dialing.
 * @param {PhoneCallCreateRequest} body
 * @returns {Promise<PhoneCall>}
 */
export async function createPhoneCall(body) {
  const { data } = await axios.post(`${PHONE_CALLS}/`, body)
  return data
}

/**
 * GET /api/work/{workId}/live-transcript-segments/ — live call lines saved after the given sequence,
 * plus the status of each call's recording transcript.
 * @param {number} workId
 * @param {number} after
 * @returns {Promise<LiveTranscriptResponse>}
 */
export async function getLiveTranscript(workId, after) {
  const { data } = await axios.get(`/api/work/${workId}/live-transcript-segments/`, { params: { after } })
  return data
}

/**
 * GET /api/telehealth/phone-calls/{workId}/ — form configuration, saved details, phone sessions and transcript.
 * @param {number} workId
 * @returns {Promise<PhoneCall>}
 */
export async function getPhoneCall(workId) {
  const { data } = await axios.get(`${PHONE_CALLS}/${workId}/`)
  return data
}

/**
 * PATCH /api/telehealth/phone-calls/{workId}/details/ — keys are field ids from the phone_call form configuration.
 * @param {number} workId
 * @param {PhoneCallDetailsUpdate} body
 * @returns {Promise<PhoneCall>}
 */
export async function updatePhoneCallDetails(workId, body) {
  const { data } = await axios.patch(`${PHONE_CALLS}/${workId}/details/`, body)
  return data
}

// ─── Inbound calls ──────────────────────────────────────────────────────

const INBOUND_CALLS = '/api/inbound-calls'

/**
 * GET /api/inbound-calls/ -- recent inbound calls.
 * @returns {Promise<InboundCall[]>}
 */
export async function listInboundCalls() {
  const { data } = await axios.get(`${INBOUND_CALLS}/`)
  return data
}

/**
 * GET /api/inbound-calls/{uuid}/
 * @param {string} uuid
 * @returns {Promise<InboundCall>}
 */
export async function getInboundCall(uuid) {
  const { data } = await axios.get(`${INBOUND_CALLS}/${uuid}/`)
  return data
}

/**
 * GET /api/inbound-calls/by-call-sid/?call_sid= — the inbound call behind this browser's call leg.
 * @param {InboundCallByCallSidQuery} params
 * @returns {Promise<InboundCall>}
 */
export async function getInboundCallByCallSid(params) {
  const { data } = await axios.get(`${INBOUND_CALLS}/by-call-sid/`, { params })
  return data
}

/**
 * POST /api/inbound-calls/{uuid}/link-patient/ — creates the call's Work as what the nurse picked:
 * an engagement type from the task's engagement_types (workTypeId), or a Care Coordination Call when omitted.
 * @param {string} uuid
 * @param {{ entityId: number, taskId?: number, workTypeId?: number }} params
 * @returns {Promise<InboundCallWorkResponse>}
 */
/**
 * POST /api/inbound-calls/{uuid}/link-task/{taskId}/
 * @param {string} uuid
 * @param {number} taskId
 * @returns {Promise<InboundCallWorkResponse>}
 */
export async function linkInboundCallTask(uuid, taskId) {
  const { data } = await axios.post(`${INBOUND_CALLS}/${uuid}/link-task/${taskId}/`)
  return data
}

export async function linkInboundCallPatient(uuid, { entityId, taskId, workTypeId, noTask = false }) {
  /** @type {Schemas['InboundCallLinkPatientRequest']} */
  const body = { entity_id: entityId }
  if (noTask) body.no_task = true
  if (taskId) body.task_id = taskId
  if (workTypeId) body.work_type_id = workTypeId
  const { data } = await axios.post(`${INBOUND_CALLS}/${uuid}/link-patient/`, body)
  return data
}

/**
 * @typedef {{
 *   opportunity_id: number,
 *   metric_name: string | null,
 *   valid_from: string,
 *   valid_thru: string | null,
 *   completed_at: string | null,
 *   task: { task_id: number, task_type_name: string | null, status_name: string | null, due_date: string | null, activated_at: string | null } | null,
 * }} PatientOpportunity
 */

/**
 * GET /api/inbound-calls/patient-opportunities/?entity_id=
 * @param {number} entityId
 * @returns {Promise<{ active: PatientOpportunity[], completed: PatientOpportunity[] }>}
 */
export async function getPatientOpportunities(entityId) {
  const { data } = await axios.get(`${INBOUND_CALLS}/patient-opportunities/`, {
    params: { entity_id: entityId },
  })
  return data
}

/**
 * POST /api/inbound-calls/{uuid}/call-type/ — chart the linked call as one of its call_types
 * (Care Coordination Call or an engagement for its task), in any direction.
 * @param {string} uuid
 * @param {number} workTypeId
 * @returns {Promise<InboundCallWorkResponse>}
 */
export async function setInboundCallType(uuid, workTypeId) {
  /** @type {Schemas['InboundCallSetCallTypeRequest']} */
  const body = { work_type_id: workTypeId }
  const { data } = await axios.post(`${INBOUND_CALLS}/${uuid}/call-type/`, body)
  return data
}
